// Admin support inbox — the triage queue (PROP-163, design source
// PROP-153). Status tabs (New / Replied / Closed / All) over the same
// paginated /admin/support list endpoint; each row links to the thread
// detail page where an operator reads the timeline and replies.
//
// Aligned with PropertyReviews.tsx for chrome (PageHeader, tab strip,
// table styles, cursor "load older" pagination, alive-guard fetch); no
// parallel design system.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, ghostBtn } from "@/components/admin-ui";
import { useToast } from "@/components/ui-prims";
import {
  listSupportThreads,
  type SupportFilter,
  type SupportThread,
} from "@/lib/support-api";
import { SupportStatusPill } from "./status-pill";

const TABS: { key: SupportFilter; label: string }[] = [
  { key: "new", label: "New" },
  { key: "replied", label: "Replied" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

const PAGE_LIMIT = 30;

export function SupportInboxPage() {
  const toast = useToast();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<SupportFilter>("new");
  const [rows, setRows] = useState<SupportThread[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadingMore, setLoadingMore] = useState(false);

  // Refresh whenever the active tab changes. The `alive` token guards
  // against a stale resolution: if the operator switched tabs (or
  // unmounted) before the fetch returned, we commit nothing.
  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setRows([]);
    setNextCursor(undefined);
    listSupportThreads({ status: filter, limit: PAGE_LIMIT })
      .then((resp) => {
        if (!alive) return;
        setRows(resp.threads ?? []);
        setNextCursor(resp.next_cursor);
        setStatus("ready");
      })
      .catch((err) => {
        if (!alive) return;
        console.error("listSupportThreads", err);
        setStatus("error");
      });
    return () => {
      alive = false;
    };
  }, [filter]);

  const onLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const resp = await listSupportThreads({
        status: filter,
        limit: PAGE_LIMIT,
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...(resp.threads ?? [])]);
      setNextCursor(resp.next_cursor);
    } catch (err) {
      console.error("listSupportThreads more", err);
      toast("Couldn't load more threads", { tone: "error" });
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <PageHeader
        kicker="Support"
        title="Inbox"
        sub="Inbound support email grouped into threads. New threads need a first reply; Replied are awaiting the user; Closed are resolved. Open a thread to read the full correspondence and reply."
      />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, padding: "0 24px", borderBottom: "1px solid var(--rule)" }}>
        {TABS.map((tab) => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                cursor: "pointer",
                fontFamily: "var(--font-ui)",
                fontSize: 12.5,
                fontWeight: active ? 500 : 400,
                color: active ? "var(--accent)" : "var(--ink-3)",
                borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div style={{ padding: "16px 24px 32px" }}>
        {status === "loading" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Loading threads…</p>
        ) : status === "error" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)" }}>
            Couldn't load threads. Refresh the page to try again.
          </p>
        ) : rows.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>
            Nothing in this queue right now.
          </p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-ui)" }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: 10.5, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                <th style={th()}>From</th>
                <th style={{ ...th(), width: "44%" }}>Subject</th>
                <th style={th()}>Messages</th>
                <th style={th()}>Status</th>
                <th style={th()}>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  className="ops-row"
                  onClick={() => navigate(`/support/inbox/${encodeURIComponent(t.id)}`)}
                  style={{ borderTop: "1px solid var(--rule)" }}
                >
                  <td style={td()}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink)", overflowWrap: "anywhere" }}>
                      {t.from_name || t.from_email || "Unknown sender"}
                    </div>
                    {t.from_name && (
                      <div style={{ fontSize: 10.5, color: "var(--ink-3)", overflowWrap: "anywhere" }}>{t.from_email}</div>
                    )}
                  </td>
                  <td style={{ ...td(), fontSize: 12.5, color: "var(--ink-2)" }}>
                    <span style={{ overflowWrap: "anywhere" }}>{t.subject || "(no subject)"}</span>
                  </td>
                  <td style={{ ...td(), fontSize: 12, color: "var(--ink-3)", fontFamily: "var(--font-mono)" }}>
                    {t.message_count}
                  </td>
                  <td style={td()}>
                    <SupportStatusPill status={t.status} />
                  </td>
                  <td style={{ ...td(), fontSize: 11.5, color: "var(--ink-3)" }}>
                    {relativeTime(t.last_message_at ?? t.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {nextCursor && status === "ready" && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <button type="button" style={ghostBtn} onClick={() => void onLoadMore()} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load older threads"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function th(): React.CSSProperties {
  return { padding: "10px 8px", borderBottom: "1px solid var(--rule)", fontWeight: 500 };
}

function td(): React.CSSProperties {
  return { padding: "12px 8px", verticalAlign: "top" };
}

// relativeTime: inline copy of the moderation surface's helper so this
// page stays self-contained and reads timestamps identically.
function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffMin = Math.max(0, (Date.now() - t) / 60000);
  if (diffMin < 60) return `${Math.round(diffMin)} min ago`;
  const diffHr = diffMin / 60;
  if (diffHr < 24) return `${Math.round(diffHr)} hrs ago`;
  const diffDay = diffHr / 24;
  if (diffDay < 7) return `${Math.round(diffDay)} days ago`;
  return new Date(iso).toLocaleDateString();
}
